/**
 * RedirectManager — admin tool for bulk-importing legacy slug → canonical
 * redirects with smart token-scored auto-routing.
 *
 * Workflow:
 *   1. Admin pastes slugs (one per line) or CSV from Google Search Console.
 *   2. "Preview" runs a dry-run that shows where each slug would route + why
 *      (auto-routed / unroutable / skipped existing / conflict).
 *   3. "Seed" commits the routes that resolved cleanly.
 *
 * Cost-safe: no LLM calls — uses backend's deterministic token-overlap scoring.
 */
import { useEffect, useState } from "react";
import {
    Link as LinkIcon,
    Loader2,
    Trash2,
    Sparkles,
    AlertTriangle,
    CheckCircle2,
    Upload,
    RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import {
    adminListRedirects,
    adminBulkSeedRedirects,
    adminDeleteRedirect,
} from "@/lib/api";
import SeoHealthTile from "./SeoHealthTile";
import SeoPerformanceTile from "./SeoPerformanceTile";

const PLACEHOLDER = `Paste one slug per line (or a CSV from Google Search Console):

water-intrusion
foundation-issues
old-roof-repair-cost
my-old-blog-url
`;

const ACTION_STYLE = {
    create: {
        label: "Will redirect",
        cls: "text-[var(--bl-green)] bg-[var(--bl-green-tint)]",
        Icon: CheckCircle2,
    },
    skip: {
        label: "Skipped",
        cls: "text-bl-muted bg-bl-bg/60",
        Icon: AlertTriangle,
    },
    unroutable: {
        label: "No match",
        cls: "text-[var(--bl-rust)] bg-[var(--bl-rust-tint)]",
        Icon: AlertTriangle,
    },
};

export default function RedirectManager() {
    const [redirects, setRedirects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [input, setInput] = useState("");
    const [previewResults, setPreviewResults] = useState(null);
    const [previewBusy, setPreviewBusy] = useState(false);
    const [seedBusy, setSeedBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await adminListRedirects();
            setRedirects(r.redirects || []);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Failed to load redirects.",
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    // Parse pasted text. Each non-empty line becomes one slug. If a line has a
    // comma we treat the first cell as the slug (lets you paste a CSV directly).
    const parseInput = () => {
        return input
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line) => line.split(",")[0].trim())
            .filter(Boolean);
    };

    const runPreview = async () => {
        const slugs = parseInput();
        if (slugs.length === 0) {
            toast.error("Paste at least one slug.");
            return;
        }
        setPreviewBusy(true);
        try {
            const r = await adminBulkSeedRedirects({ slugs, dry_run: true });
            setPreviewResults(r);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Preview failed.",
            );
        } finally {
            setPreviewBusy(false);
        }
    };

    const commit = async () => {
        const slugs = parseInput();
        if (slugs.length === 0) {
            toast.error("Paste at least one slug.");
            return;
        }
        setSeedBusy(true);
        try {
            const r = await adminBulkSeedRedirects({ slugs, dry_run: false });
            toast.success(
                `${r.created} redirect${r.created === 1 ? "" : "s"} created · ${r.skipped_existing} skipped · ${r.unroutable} unroutable.`,
            );
            setPreviewResults(null);
            setInput("");
            await load();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Seed failed.",
            );
        } finally {
            setSeedBusy(false);
        }
    };

    const remove = async (slug) => {
        if (!window.confirm(`Delete redirect for "${slug}"?`)) return;
        try {
            await adminDeleteRedirect(slug);
            toast.success(`Removed redirect for ${slug}.`);
            await load();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Delete failed.",
            );
        }
    };

    return (
        <div className="space-y-5" data-testid="redirect-manager">
            <SeoHealthTile />
            <SeoPerformanceTile />
            <div className="bl-card p-5 space-y-4">
                <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-bl-primary" />
                    <h2 className="font-display font-semibold text-base text-bl tracking-tight">
                        Bulk seed legacy slug redirects
                    </h2>
                </div>
                <p className="text-xs text-bl-muted leading-relaxed">
                    Paste any legacy / external slugs (one per line, or a CSV from
                    Google Search Console). The seeder auto-routes each to the
                    best-matching published article using deterministic
                    token-overlap scoring — no AI, no extra cost. Run a Preview
                    first to inspect proposed routes before committing.
                </p>
                <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={PLACEHOLDER}
                    className="w-full bl-input min-h-[140px] text-xs font-mono-bl resize-y"
                    data-testid="redirect-input"
                />
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={runPreview}
                        disabled={previewBusy || !input.trim()}
                        className="bl-btn-ghost text-xs gap-1"
                        data-testid="redirect-preview"
                    >
                        {previewBusy ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <RefreshCw className="h-3 w-3" />
                        )}
                        Preview routing
                    </button>
                    <button
                        type="button"
                        onClick={commit}
                        disabled={seedBusy || !input.trim()}
                        className="bl-btn-primary text-xs gap-1"
                        data-testid="redirect-seed"
                    >
                        {seedBusy ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <Upload className="h-3 w-3" />
                        )}
                        Seed redirects
                    </button>
                </div>

                {previewResults && (
                    <div
                        className="rounded border border-bl/40 mt-2"
                        data-testid="redirect-preview-results"
                    >
                        <div className="px-3 py-2 border-b border-bl/30 text-[10px] uppercase tracking-[0.18em] text-bl-muted flex items-center gap-3">
                            <span>Preview</span>
                            <span className="text-bl">
                                {previewResults.created} will create
                            </span>
                            <span>{previewResults.skipped_existing} skip</span>
                            <span className="text-[var(--bl-rust)]">
                                {previewResults.unroutable} unroutable
                            </span>
                        </div>
                        <div className="max-h-72 overflow-y-auto">
                            {(previewResults.results || []).map((r, idx) => {
                                const style =
                                    ACTION_STYLE[r.action] || ACTION_STYLE.skip;
                                const Icon = style.Icon;
                                return (
                                    <div
                                        key={`${r.from}-${idx}`}
                                        className="px-3 py-2 border-b border-bl/30 last:border-0 flex items-center gap-3 text-xs"
                                    >
                                        <span
                                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${style.cls}`}
                                        >
                                            <Icon className="h-3 w-3" />
                                            {style.label}
                                        </span>
                                        <code className="text-bl flex-shrink-0 font-mono-bl">
                                            /learn/{r.from}
                                        </code>
                                        {r.to && (
                                            <>
                                                <span className="text-bl-muted">→</span>
                                                <code className="text-bl-muted truncate font-mono-bl">
                                                    /learn/{r.to}
                                                </code>
                                            </>
                                        )}
                                        <span className="text-bl-muted ml-auto text-[10px] italic">
                                            {r.reason}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            <div className="bl-card p-5">
                <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2">
                        <LinkIcon className="h-4 w-4 text-bl-primary" />
                        <h2 className="font-display font-semibold text-base text-bl tracking-tight">
                            Active redirects ({redirects.length})
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={load}
                        disabled={loading}
                        className="bl-btn-ghost text-xs gap-1"
                        data-testid="redirect-refresh"
                    >
                        <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                </div>

                {loading ? (
                    <div className="py-6 text-center text-sm text-bl-muted">
                        <Loader2 className="h-4 w-4 mx-auto animate-spin mb-1" />
                        Loading…
                    </div>
                ) : redirects.length === 0 ? (
                    <p className="text-xs text-bl-muted italic">
                        No legacy redirects yet. Paste slugs above to seed some.
                    </p>
                ) : (
                    <div className="rounded border border-bl/40 max-h-96 overflow-y-auto">
                        {redirects.map((r) => (
                            <div
                                key={r.slug}
                                className="px-3 py-2 border-b border-bl/30 last:border-0 flex items-center gap-3 text-xs"
                                data-testid={`redirect-row-${r.slug}`}
                            >
                                <code className="text-bl flex-shrink-0 font-mono-bl">
                                    /learn/{r.slug}
                                </code>
                                <span className="text-bl-muted">→</span>
                                <code className="text-bl-muted truncate font-mono-bl flex-1">
                                    /learn/{r.redirect_to}
                                </code>
                                <button
                                    type="button"
                                    onClick={() => remove(r.slug)}
                                    className="bl-btn-ghost text-[11px] gap-1 text-[var(--bl-rust)]"
                                    data-testid={`redirect-delete-${r.slug}`}
                                >
                                    <Trash2 className="h-3 w-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
